import React, { Suspense, useRef, useState } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Float, MeshDistortMaterial, Sparkles } from '@react-three/drei';
import { useReducedMotion } from 'framer-motion';

// Cursor-driven parallax: the whole rig tilts toward the pointer instead of
// spinning on a fixed timer, so it reads as a responsive object, not a GIF.
function PointerRig({ children, intensity = 0.3 }) {
  const group = useRef(null);
  const target = useRef({ x: 0, y: 0 });

  useFrame((state) => {
    if (!group.current) return;
    target.current.x = (state.pointer.x * intensity);
    target.current.y = (state.pointer.y * intensity * 0.6);
    group.current.rotation.y += (target.current.x - group.current.rotation.y) * 0.05;
    group.current.rotation.x += (-target.current.y - group.current.rotation.x) * 0.05;
  });

  return <group ref={group}>{children}</group>;
}

function CoreShape({ color, distort, speed, wireframe }) {
  const mesh = useRef(null);
  useFrame((_, delta) => {
    if (mesh.current) mesh.current.rotation.z += delta * 0.06;
  });
  return (
    <mesh ref={mesh}>
      <icosahedronGeometry args={[1.3, 1]} />
      {wireframe ? (
        <meshBasicMaterial color={color} wireframe transparent opacity={0.35} />
      ) : (
        <MeshDistortMaterial
          color={color}
          distort={distort}
          speed={speed}
          roughness={0.15}
          metalness={0.65}
        />
      )}
    </mesh>
  );
}

function OrbitRing({ radius, color, tilt, duration }) {
  const ring = useRef(null);
  useFrame((state) => {
    if (ring.current) {
      const t = state.clock.getElapsedTime();
      ring.current.rotation.z = (t / duration) * Math.PI * 2;
    }
  });
  return (
    <group rotation={[tilt, 0, 0]}>
      <mesh ref={ring}>
        <torusGeometry args={[radius, 0.015, 16, 100]} />
        <meshBasicMaterial color={color} transparent opacity={0.5} />
      </mesh>
    </group>
  );
}

function HeroRig() {
  return (
    <PointerRig intensity={0.5}>
      <Float speed={1.4} rotationIntensity={0.5} floatIntensity={0.9}>
        <CoreShape color="#FD8112" distort={0.35} speed={1.6} />
      </Float>
      <OrbitRing radius={2.1} color="#FFA954" tilt={0.5} duration={14} />
      <OrbitRing radius={2.5} color="#FF5F6D" tilt={-0.35} duration={20} />
      <Sparkles count={40} scale={5} size={2} speed={0.3} color="#FFD9A0" />
    </PointerRig>
  );
}

function FooterRig() {
  return (
    <PointerRig intensity={0.2}>
      <Float speed={1} rotationIntensity={0.4} floatIntensity={0.6}>
        <CoreShape color="#FD8112" distort={0.25} speed={1} wireframe />
      </Float>
      <OrbitRing radius={1.8} color="#FF5F6D" tilt={0.4} duration={18} />
    </PointerRig>
  );
}

const RIGS = { hero: HeroRig, footer: FooterRig };

// Real WebGL scene (three.js via @react-three/fiber). Falls back to a plain
// glow div for reduced-motion users so nothing spins for people who opt out.
export default function Scene3D({ variant = 'hero', style, className }) {
  const prefersReducedMotion = useReducedMotion();
  const [lostContext, setLostContext] = useState(false);
  const Rig = RIGS[variant] || HeroRig;

  if (prefersReducedMotion || lostContext) {
    return (
      <div
        aria-hidden="true"
        className={className}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(circle at 50% 45%, rgba(253,129,18,0.35), transparent 65%)',
          ...style,
        }}
      />
    );
  }

  return (
    <Canvas
      aria-hidden="true"
      className={className}
      dpr={[1, 1.5]}
      camera={{ position: [0, 0, 6], fov: 45 }}
      style={{ position: 'absolute', inset: 0, ...style }}
      onCreated={({ gl }) => {
        gl.domElement.addEventListener('webglcontextlost', (e) => {
          e.preventDefault();
          setLostContext(true);
        });
      }}
    >
      <ambientLight intensity={0.5} />
      <pointLight position={[4, 4, 4]} intensity={1.4} color="#FFA954" />
      <pointLight position={[-4, -2, -2]} intensity={0.6} color="#FF5F6D" />
      <Suspense fallback={null}>
        <Rig />
      </Suspense>
    </Canvas>
  );
}
